import { Bloom, EffectComposer, ChromaticAberration, Vignette } from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import { Vector2 } from 'three';
import useStore from '../../store/useStore';
import { KawaseLightStreak } from './KawaseLightStreak';

/**
 * Post-processing stack.
 *
 * Gem streaks: two-pass Kawase "star" sparkle off bright gem facets — only in DARK mode with
 *              gems visible. In light mode the metal (full envMapIntensity, un-tone-mapped) blows
 *              past the high-pass threshold and the streak smears the whole ring white; dark mode
 *              dims the metal (env 0.15) so only the gems cross it. Emitting-lines modes skip it.
 * Light mode:  subtle vignette + very light bloom on specular highlights.
 * Dark mode:   strong bloom (feeds off fluorescent material emissive),
 *              slight chromatic aberration for a neon-sign feel.
 */
export default function PostEffects() {
    const isDark = useStore((s) => s.isDark);
    const streaksOn = useStore((s) => s.styleMode) === 'gems' && isDark;

    return (
        <EffectComposer
            multisampling={4}
            /* Rebuild the composer when the streak passes appear/disappear. R3F appends
             * postprocessing passes in source order on a fresh mount, but on an in-place update
             * its insertBefore pushes new passes to the END of the list — which would drop the
             * gem streaks after the vignette. Keying forces a clean rebuild so the declared order
             * (streaks -> bloom -> aberration -> vignette) always holds. */
            key={streaksOn ? 'fx-streaks' : 'fx-no-streaks'}
        >
            {/* Gem light streaks — threshold sits below the >1 gem facet peaks so only bright
                gem highlights streak (the dark HDR surround stays under it). */}
            <KawaseLightStreak enabled={streaksOn} threshold={1.4} attenuation={0.9} blurPasses={4} opacity={0.3} />
            <Bloom
                intensity={isDark ? 2.2 : 0.4}
                luminanceThreshold={isDark ? 0.9 : 0.05}
                luminanceSmoothing={0.4}
                mipmapBlur
            />
            {isDark ? (
                <ChromaticAberration
                    blendFunction={BlendFunction.NORMAL}
                    offset={new Vector2(0.0008, 0.0008)}
                    radialModulation={false}
                    modulationOffset={0}
                />
            ) : <></>}
            <Vignette
                offset={0.3}
                darkness={isDark ? 0.8 : 0.4}
                eskil={false}
                blendFunction={BlendFunction.NORMAL}
            />
        </EffectComposer>
    );
}
